import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
    Box, Container, Typography, Card, CardContent, Grid,
    Button, Chip, List, ListItem, ListItemIcon, ListItemText,
    Divider, CircularProgress, Alert,
} from '@mui/material';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import StarIcon from '@mui/icons-material/Star';
import RocketLaunchIcon from '@mui/icons-material/RocketLaunch';
import RadioButtonUncheckedIcon from '@mui/icons-material/RadioButtonUnchecked';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import DowngradeConfirmModal from './DowngradeConfirmModal';
import { api } from '../../utils/api';
import { trackEvent } from '../../utils/analytics';

export type PlanTier = 'free' | 'pro' | 'premium';

interface PlanFeature { label: string; }

interface Plan {
    name: string;
    tier: PlanTier;
    tagline: string;
    badge?: string;
    badgeColor?: string;
    cta: string;
    highlight: boolean;
    comingSoon?: boolean;
    features: PlanFeature[];
    icon: React.ReactNode;
    storage: string;
}

const plans: Plan[] = [
    {
        name: 'Free',
        tier: 'free',
        tagline: 'Start documenting your travels at no cost.',
        cta: 'Get Started Free',
        highlight: false,
        storage: '1 GB storage',
        icon: <RadioButtonUncheckedIcon sx={{ fontSize: 28, color: '#2D6E6A' }} />,
        features: [
            { label: 'Create & import trips' },
            { label: 'Trip itineraries' },
            { label: 'Travel photo gallery' },
            { label: 'Friends & sharing' },
            { label: 'Public & Friends visibility' },
            { label: 'Community access' },
            { label: '1 GB photo storage' },
        ],
    },
    {
        name: 'Pro',
        tier: 'pro',
        tagline: 'More control, more sharing, more memories.',
        badge: 'Free for a limited time',
        badgeColor: '#2D6E6A',
        cta: 'Join Early Access',
        highlight: true,
        storage: '15 GB storage',
        icon: <StarIcon sx={{ fontSize: 28, color: 'white' }} />,
        features: [
            { label: 'Everything in Free' },
            { label: '15 GB photo storage' },
            { label: 'Trip companions (up to 6)' },
            { label: 'Expiration control' },
            { label: 'Expiration notifications' },
            { label: 'BFFs / Travel Partners visibility' },
            { label: 'Photo approval workflow' },
        ],
    },
    {
        name: 'Premium',
        tier: 'premium',
        tagline: 'The full Percoro experience.',
        badge: 'Coming Soon',
        badgeColor: '#9e9e9e',
        cta: 'Notify Me',
        highlight: false,
        comingSoon: true,
        storage: '50 GB storage',
        icon: <RocketLaunchIcon sx={{ fontSize: 28, color: '#9e9e9e' }} />,
        features: [
            { label: 'Everything in Pro' },
            { label: '50 GB photo storage' },
            { label: 'Unlimited trip companions' },
            { label: 'Group trips' },
            { label: 'Early access to new features' },
        ],
    },
];

interface PlansSectionProps {
    /** Pass currentTier when rendering in authenticated context (/plans page).
     *  Omit on landing page — CTAs go to /signup instead. */
    currentPlan?: PlanTier | null;
    /** Called after a successful upgrade/downgrade request */
    onRequestSuccess?: (requestedPlan: PlanTier) => void;
}

const PlansSection: React.FC<PlansSectionProps> = ({ currentPlan, onRequestSuccess }) => {
    const navigate = useNavigate();
    const [loading, setLoading] = useState<PlanTier | null>(null);
    const [success, setSuccess] = useState<PlanTier | null>(null);
    const [error, setError] = useState<string | null>(null);
    const [downgradeTarget, setDowngradeTarget] = useState<PlanTier | null>(null);

    const tierOrder: Record<PlanTier, number> = { free: 0, pro: 1, premium: 2 };

    const isCurrentPlan = (tier: PlanTier) => currentPlan === tier;

    const isDowngrade = (tier: PlanTier) =>
        currentPlan !== null && currentPlan !== undefined &&
        tierOrder[tier] < tierOrder[currentPlan];

    const handleCta = (plan: Plan) => {
        // Landing page (no currentPlan) — go to signup
        if (currentPlan === null || currentPlan === undefined) {
            if (!plan.comingSoon) navigate('/signup');
            return;
        }
        // Already on this plan
        if (isCurrentPlan(plan.tier)) return;
        // Coming soon
        if (plan.comingSoon) return;
        // Downgrade — show confirmation modal
        if (isDowngrade(plan.tier)) {
            setDowngradeTarget(plan.tier);
            return;
        }
        // Upgrade — submit directly
        submitRequest(plan.tier, false);
    };

    const submitRequest = async (requestedPlan: PlanTier, acknowledgedDowngrade: boolean) => {
        setLoading(requestedPlan);
        setError(null);
        try {
            const res = await api.post('/users/plan/upgrade-request', {
                requested_plan: requestedPlan,
                acknowledged_downgrade: acknowledgedDowngrade,
            });
            const data = await res.json();
            if (data.success) {
                setSuccess(requestedPlan);
                trackEvent('plan_upgrade_requested', { plan: requestedPlan });
                onRequestSuccess?.(requestedPlan);
            } else {
                setError(data.error || 'Something went wrong. Please try again.');
            }
        } catch {
            setError('Network error. Please try again.');
        } finally {
            setLoading(null);
        }
    };

    const handleDowngradeConfirm = async () => {
        if (!downgradeTarget) return;
        await submitRequest(downgradeTarget, true);
        setDowngradeTarget(null);
    };

    const getCtaLabel = (plan: Plan): string => {
        if (!currentPlan) return plan.cta;
        if (isCurrentPlan(plan.tier)) return 'Your Current Plan';
        if (success === plan.tier) return 'Request Received ✓';
        if (isDowngrade(plan.tier)) return 'Downgrade to ' + plan.name;
        return plan.cta;
    };

    const getCtaVariant = (plan: Plan): 'contained' | 'outlined' | 'text' => {
        if (isCurrentPlan(plan.tier)) return 'outlined';
        if (plan.highlight) return 'contained';
        return 'outlined';
    };

    return (
        <Box sx={{ py: { xs: 8, md: 12 }, bgcolor: 'background.default' }}>
            <Container maxWidth="lg">
                <Box sx={{ textAlign: 'center', mb: { xs: 6, md: 8 } }}>
                    <Typography variant="h2" sx={{ fontWeight: 700, mb: 2, color: 'text.primary' }}>
                        Simple, transparent plans
                    </Typography>
                    <Typography sx={{ color: 'text.secondary', maxWidth: '560px', mx: 'auto', fontSize: '1.1rem' }}>
                        Start free. Early access users get Pro free for a limited time.
                    </Typography>
                </Box>

                {error && (
                    <Alert severity="error" sx={{ mb: 3, borderRadius: '12px', maxWidth: 560, mx: 'auto' }} onClose={() => setError(null)}>
                        {error}
                    </Alert>
                )}

                <Grid container spacing={3} alignItems="stretch">
                    {plans.map((plan) => {
                        const isCurrent = isCurrentPlan(plan.tier);
                        const isSucceeded = success === plan.tier;
                        const isLoading = loading === plan.tier;

                        return (
                            <Grid item xs={12} md={4} key={plan.name}>
                                <Card sx={{
                                    height: '100%',
                                    borderRadius: '20px',
                                    border: isCurrent
                                        ? '2px solid #2D6E6A'
                                        : plan.highlight
                                            ? '2px solid #2D6E6A'
                                            : '1px solid #e0e0e0',
                                    boxShadow: plan.highlight
                                        ? '0 8px 32px rgba(45,110,106,0.15)'
                                        : '0 2px 12px rgba(0,0,0,0.06)',
                                    opacity: plan.comingSoon ? 0.8 : 1,
                                    position: 'relative',
                                    overflow: 'visible',
                                }}>
                                    {/* Badge */}
                                    {isCurrent ? (
                                        <Box sx={{ position: 'absolute', top: -14, left: '50%', transform: 'translateX(-50%)', zIndex: 1 }}>
                                            <Chip
                                                label="Your Current Plan"
                                                size="small"
                                                icon={<CheckCircleOutlineIcon sx={{ fontSize: '0.9rem !important', color: 'white !important' }} />}
                                                sx={{ bgcolor: '#2D6E6A', color: 'white', fontWeight: 700, fontSize: '0.75rem', px: 1 }}
                                            />
                                        </Box>
                                    ) : plan.badge ? (
                                        <Box sx={{ position: 'absolute', top: -14, left: '50%', transform: 'translateX(-50%)', zIndex: 1 }}>
                                            <Chip label={plan.badge} size="small"
                                                sx={{ bgcolor: plan.badgeColor, color: 'white', fontWeight: 700, fontSize: '0.75rem', px: 1 }} />
                                        </Box>
                                    ) : null}

                                    <CardContent sx={{ p: 4 }}>
                                        {/* Icon */}
                                        <Box sx={{
                                            bgcolor: plan.highlight ? '#2D6E6A' : isCurrent ? '#E8F5F3' : plan.comingSoon ? '#f5f5f5' : '#f0faf9',
                                            borderRadius: '50%', width: 56, height: 56,
                                            display: 'flex', alignItems: 'center', justifyContent: 'center',
                                            mb: 2,
                                            border: plan.highlight ? 'none' : `2px solid ${isCurrent ? '#2D6E6A' : plan.comingSoon ? '#e0e0e0' : '#2D6E6A'}`,
                                        }}>
                                            {plan.icon}
                                        </Box>

                                        <Typography variant="h5" sx={{ fontWeight: 700, mb: 0.5 }}>{plan.name}</Typography>
                                        <Typography variant="body2" color="text.secondary" sx={{ mb: 1, lineHeight: 1.5 }}>
                                            {plan.tagline}
                                        </Typography>
                                        <Chip label={plan.storage} size="small"
                                            sx={{
                                                bgcolor: plan.highlight ? '#e8f5f3' : '#f5f5f5',
                                                color: plan.highlight ? '#2D6E6A' : '#757575',
                                                fontWeight: 600, fontSize: '0.75rem', mb: 2,
                                            }} />
                                        <Divider sx={{ mb: 2 }} />
                                        <List dense disablePadding sx={{ mb: 3 }}>
                                            {plan.features.map((f, i) => (
                                                <ListItem key={i} disablePadding sx={{ py: 0.4 }}>
                                                    <ListItemIcon sx={{ minWidth: 28 }}>
                                                        <CheckCircleIcon sx={{
                                                            fontSize: 18,
                                                            color: plan.highlight || isCurrent ? '#2D6E6A' : plan.comingSoon ? '#9e9e9e' : '#2D6E6A',
                                                        }} />
                                                    </ListItemIcon>
                                                    <ListItemText primary={f.label}
                                                        primaryTypographyProps={{ variant: 'body2', sx: { fontSize: '0.875rem' } }} />
                                                </ListItem>
                                            ))}
                                        </List>

                                        {/* CTA */}
                                        <Button
                                            fullWidth
                                            variant={getCtaVariant(plan)}
                                            onClick={() => handleCta(plan)}
                                            disabled={isCurrent || plan.comingSoon || isLoading || isSucceeded}
                                            sx={{
                                                borderRadius: '24px', textTransform: 'none', fontWeight: 600, py: 1.25,
                                                bgcolor: plan.highlight && !isCurrent ? '#2D6E6A' : undefined,
                                                borderColor: isCurrent ? '#2D6E6A' : !plan.highlight ? '#2D6E6A' : undefined,
                                                color: plan.highlight && !isCurrent ? 'white'
                                                    : isCurrent ? '#2D6E6A'
                                                        : !plan.comingSoon ? '#2D6E6A' : undefined,
                                                '&:hover': plan.highlight && !isCurrent ? { bgcolor: '#1F4E4A' } : {},
                                                '&.Mui-disabled': isCurrent
                                                    ? { borderColor: '#2D6E6A', color: '#2D6E6A', opacity: 0.8 }
                                                    : {},
                                            }}
                                        >
                                            {isLoading
                                                ? <CircularProgress size={18} sx={{ color: 'inherit' }} />
                                                : getCtaLabel(plan)}
                                        </Button>

                                        {/* Success message */}
                                        {isSucceeded && (
                                            <Typography variant="caption" sx={{ display: 'block', textAlign: 'center', mt: 1.5, color: '#2D6E6A', fontWeight: 600 }}>
                                                Request received! We'll upgrade your account shortly.
                                            </Typography>
                                        )}

                                        {/* Downgrade hint for Pro users on Free card */}
                                        {currentPlan && isDowngrade(plan.tier) && !isSucceeded && (
                                            <Typography variant="caption" sx={{ display: 'block', textAlign: 'center', mt: 1, color: 'text.secondary' }}>
                                                You'll be asked to confirm before downgrading
                                            </Typography>
                                        )}
                                    </CardContent>
                                </Card>
                            </Grid>
                        );
                    })}
                </Grid>

                <Typography variant="body2" sx={{ textAlign: 'center', mt: 4, color: 'text.secondary' }}>
                    No credit card required. Early access users unlock Pro free for a limited time.
                </Typography>
            </Container>

            {/* Downgrade confirmation modal */}
            <DowngradeConfirmModal
                open={downgradeTarget !== null}
                onClose={() => setDowngradeTarget(null)}
                fromPlan={currentPlan || 'pro'}
                toPlan={downgradeTarget || 'free'}
                onConfirm={handleDowngradeConfirm}
            />
        </Box>
    );
};

export default PlansSection;
